import { useCallback, useEffect, useRef, useState } from 'react';
import { useEntityStore } from '@ContractBuilder/store';
import { useUIStore } from '@ContractBuilder/store/ui.store';
import { downloadDocument } from '@helpers/downloadDocument';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { useDocumentLocation } from '@hooks/index';
import { PDF_REFETCH_TIMEOUT_MS, usePullPdfDocumentUrl } from '@hooks/pullDocumentUrl';
import { fetchEntity } from '@queries/fetchEntity';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash-es';

import { PDFPreviewShell } from '../components/PDFPreviewShell';
import { CONTRACT_COMPARISON_TAB_ID } from '../constants';
import { useEntityAndDocumentType } from '../hooks/useEntityAndDocumentType';
import { usePrefetchPDFs } from '../hooks/usePrefetchPDFs';
import { usePDFPreviewStore } from '../store/pdf-preview.store';
import { ContractComparisonTab } from '../tabs/ContractComparisonTab';
import { LivePDFTab } from '../tabs/LivePDFTab';
import { hasActiveMalwareScan } from '../utils/has-active-malware-scan';

export const PDFPreviewController = () => {
  const { submission, reloadSubmission } = useEntityStore(({ submission, reloadSubmission }) => ({
    submission,
    reloadSubmission,
  }));
  const togglePreview = useUIStore(({ togglePDFPreview }) => togglePDFPreview);
  const setActiveTab = usePDFPreviewStore((s) => s.setActiveTab);
  const { isBlockComparisonView } = useDocumentLocation();
  const { mode } = getDocumentMode();

  // TODO Phase A: uses dataRange: [] + version-tracking key — incompatible with entityQueries.detail(). Migrate separately.
  const { data: freshSubmission } = useQuery({
    queryKey: [submission?.id, submission?.updated_at],
    queryFn: () => fetchEntity({ documentMode: mode, dataRange: [] }),
    refetchOnMount: 'always',
    enabled: submission !== undefined,
  });

  const { entity, documentName, resourceName, documentType } = useEntityAndDocumentType(freshSubmission);

  // An attachment's malware scan runs async on the backend; the contract PDF is only regenerated
  // (and its version bumped) once it completes. `isAwaitingScan` is the single signal that both
  // drives the live poll and shows the "Updating" pill, auto-expiring at the poll cap so neither
  // outlives the other if the scan never resolves.
  const isAttachmentScanPending = hasActiveMalwareScan(submission?.attachments);
  const [isAwaitingScan, setIsAwaitingScan] = useState(false);
  const reloadedVersionRef = useRef<number | undefined>(undefined);

  const {
    documentVersion,
    expectedVersion,
    refetch: refetchDocumentUrl,
    url,
    isLoading: isContractPDFLoading,
  } = usePullPdfDocumentUrl(resourceName, documentType, entity?.version ?? 1, true, isAwaitingScan);

  // Fallback to submission to avoid tab flickering when first loading the PDF drawer
  const isRenewal = (freshSubmission ?? submission)?.renewalParentId !== undefined;

  const {
    url: contractComparisonPDFUrl,
    isLoading: isContractComparisonPDFLoading,
    refetch: refetchContractComparisonPDFUrl,
    documentVersion: contractComparisonDocumentVersion,
    expectedVersion: contractComparisonExpectedVersion,
  } = usePullPdfDocumentUrl('submissions', 'submission-comparison-document', entity?.version ?? 1, isRenewal);

  usePrefetchPDFs();

  // Open (and auto-close at the poll cap) the awaiting-scan window when an attachment scan starts.
  useEffect(() => {
    if (!isAttachmentScanPending) {
      setIsAwaitingScan(false);
      reloadedVersionRef.current = undefined;
      return;
    }

    setIsAwaitingScan(true);
    const timeout = setTimeout(() => setIsAwaitingScan(false), PDF_REFETCH_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isAttachmentScanPending]);

  // While a scan is pending, reload the submission once per distinct polled version so its
  // attachments leave PENDING — which closes the window and stops the poll/pill. Reloading on the
  // first observed version (not only on a subsequent increase) also covers a late-opened drawer
  // where the very first poll already returns the post-scan version. De-duping by reloaded version
  // keeps a steady poll from reloading every tick. Gated on the freshly-derived
  // `isAttachmentScanPending` (not the lagging `isAwaitingScan` state) so that, on the flush where
  // the scan clears, this effect short-circuits before reading the ref the close effect just reset.
  useEffect(() => {
    if (!isAttachmentScanPending || !documentVersion || reloadedVersionRef.current === documentVersion) {
      return;
    }

    reloadedVersionRef.current = documentVersion;
    reloadSubmission();
  }, [documentVersion, isAttachmentScanPending, reloadSubmission]);

  useEffect(() => {
    if (isBlockComparisonView) {
      setActiveTab(CONTRACT_COMPARISON_TAB_ID);
    }
    // eslint-disable-next-line -- Only run on mount
  }, []);

  const handleDocumentDownload = useCallback(
    async <T,>(refetchFn: () => Promise<QueryObserverResult<T, Error>>, accessor: string) => {
      const response = await refetchFn();
      const parsedUrl = get(response.data, accessor);

      if (parsedUrl) {
        downloadDocument(parsedUrl);
      }
    },
    [],
  );

  return (
    <PDFPreviewShell
      isLoading={isContractPDFLoading || isContractComparisonPDFLoading}
      onClose={togglePreview}
      renderTabs={({ getTabProps }) => (
        <>
          <LivePDFTab
            {...getTabProps('live-pdf')}
            documentUrl={url}
            documentVersion={documentVersion}
            expectedVersion={expectedVersion}
            isScanPending={isAwaitingScan}
            label={documentName}
            onDownload={() => handleDocumentDownload(refetchDocumentUrl, 'data.url')}
          />

          {isRenewal && (
            <ContractComparisonTab
              {...getTabProps(CONTRACT_COMPARISON_TAB_ID)}
              documentUrl={contractComparisonPDFUrl}
              documentVersion={contractComparisonDocumentVersion}
              expectedVersion={contractComparisonExpectedVersion}
              label="Comparison Version"
              onDownload={() => handleDocumentDownload(refetchContractComparisonPDFUrl, 'data.url')}
            />
          )}
        </>
      )}
    />
  );
};
